<template>
    <div v-if="loading" class="text-sm text-slate-500" data-test="qualification-loading">Checking qualification…</div>
    <vx-alert v-else-if="qualification" :color="color" :closeable="false" data-test="qualification-summary">
        <div class="flex flex-col gap-1 text-sm">
            <div>
                <strong>{{ qualification.usecase }}</strong> · monthly fee {{ feeLabel(qualification.monthly_fee) }}
            </div>
            <div
                v-for="(terms, mnoId) in qualification.mno_metadata"
                :key="mnoId"
                :data-test="`qualification-${mnoId}`"
            >
                <strong>{{ mnoLabel(mnoId, terms.mno) }}</strong
                >: qualify {{ yesNo(terms.qualify) }} · review
                {{ yesNo(terms.mno_review) }}
                <template v-if="MNO_ATT === mnoId"> · class / TPM {{ carrierClassLabel(terms) }}</template>
                <template v-if="MNO_TMOBILE === mnoId"> · tier / daily cap {{ carrierTierLabel(terms) }}</template>
            </div>
        </div>
    </vx-alert>
    <vx-alert v-else-if="loaded" color="slate" :closeable="false" data-test="qualification-missing">
        {{ props.usecase }} is not offered for this brand.
    </vx-alert>
</template>

<script lang="ts" setup>
import { VxAlert } from '@voxie/frontend-components';
import { isCancel } from 'axios';
import { computed, ref, watch } from 'vue';
import {
    carrierClassLabel,
    carrierTierLabel,
    feeLabel,
    MNO_ATT,
    MNO_TMOBILE,
    mnoLabel,
    UNVETTED_DAILY_CAP,
    yesNo,
} from '~/components/kiosk/kiosk-ten-dlc/registration/tenDlcRegistrationLabels';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type { TcrUsecaseQualification } from '~/services/adminTenDlcRegistrationClient.types';
import { catchRequestError } from '~/utils/request';

const props = defineProps<{ brandId: string; usecase: string }>();

const toasted = useToasted();
const qualifications = ref<TcrUsecaseQualification[]>([]);
const loading = ref(false);
const loaded = ref(false);

const qualificationCancelKey = Symbol('kiosk-tcr-qualification-summary');

watch(
    () => props.brandId,
    (brandId) => {
        loading.value = true;
        loaded.value = false;
        qualifications.value = [];

        adminTenDlcRegistrationClient
            .getBrandQualification(brandId, { cancelKey: qualificationCancelKey })
            .then((response) => {
                qualifications.value = response.data.data;
                loaded.value = true;
                loading.value = false;
            })
            .catch((e) => {
                if (isCancel(e)) {
                    return;
                }

                loading.value = false;
                toasted.error(catchRequestError(e), { sticky: true });
            });
    },
    { immediate: true }
);

const qualification = computed(() => qualifications.value.find((entry) => entry.usecase === props.usecase));

// The unvetted T-Mobile tier or a carrier that declines the use case is the prompt to wait for the vet.
const color = computed(() => {
    const terms = Object.values(qualification.value?.mno_metadata ?? {});

    return UNVETTED_DAILY_CAP === qualification.value?.mno_metadata[MNO_TMOBILE]?.brand_daily_cap ||
        terms.some((entry) => false === entry.qualify)
        ? 'danger'
        : 'info';
});
</script>
